function normalizeProgress(progress) {
  return ((progress % 1) + 1) % 1
}

function segmentLength(start, end) {
  const deltaX = end.x - start.x
  const deltaY = end.y - start.y
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY)
}

function pointOnRoundedFrame(width, height, radius, progress) {
  const w = Math.max(1, width)
  const h = Math.max(1, height)
  const r = Math.max(0, Math.min(radius, w / 2, h / 2))
  const horizontal = w - r * 2
  const vertical = h - r * 2
  const arc = Math.PI * r / 2
  const total = (horizontal + vertical) * 2 + arc * 4
  let distance = normalizeProgress(progress) * total

  if (distance <= horizontal) return { x: r + distance, y: 0 }
  distance -= horizontal
  if (distance <= arc) {
    const angle = r ? -Math.PI / 2 + distance / r : 0
    return { x: w - r + Math.cos(angle) * r, y: r + Math.sin(angle) * r }
  }
  distance -= arc
  if (distance <= vertical) return { x: w, y: r + distance }
  distance -= vertical
  if (distance <= arc) {
    const angle = r ? distance / r : 0
    return { x: w - r + Math.cos(angle) * r, y: h - r + Math.sin(angle) * r }
  }
  distance -= arc
  if (distance <= horizontal) return { x: w - r - distance, y: h }
  distance -= horizontal
  if (distance <= arc) {
    const angle = r ? Math.PI / 2 + distance / r : 0
    return { x: r + Math.cos(angle) * r, y: h - r + Math.sin(angle) * r }
  }
  distance -= arc
  if (distance <= vertical) return { x: 0, y: h - r - distance }
  distance -= vertical
  const angle = r ? Math.PI + distance / r : 0
  return { x: r + Math.cos(angle) * r, y: r + Math.sin(angle) * r }
}

function buildConnectorPoints(sourceRect, targetRect, boundsRect) {
  const boundsLeft = boundsRect.left || 0
  const boundsTop = boundsRect.top || 0
  const start = {
    x: sourceRect.right - boundsLeft,
    y: sourceRect.top + sourceRect.height / 2 - boundsTop
  }
  const end = {
    x: targetRect.left - boundsLeft,
    y: targetRect.top + targetRect.height / 2 - boundsTop
  }
  const railX = start.x + (end.x - start.x) / 2

  return [
    start,
    { x: railX, y: start.y },
    { x: railX, y: end.y },
    end
  ]
}

function createFlowRenderer(options) {
  const settings = options || {}
  const browserWindow = typeof window === 'undefined' ? null : window
  const requestFrame = settings.requestFrame || (
    browserWindow && browserWindow.requestAnimationFrame
      ? browserWindow.requestAnimationFrame.bind(browserWindow)
      : browserWindow && browserWindow.setTimeout
        ? function (callback) { return browserWindow.setTimeout(function () { callback(Date.now()) }, 16) }
        : function () { return 0 }
  )
  const cancelFrame = settings.cancelFrame || (
    browserWindow && browserWindow.cancelAnimationFrame
      ? browserWindow.cancelAnimationFrame.bind(browserWindow)
      : browserWindow && browserWindow.clearTimeout
        ? browserWindow.clearTimeout.bind(browserWindow)
        : function () {}
  )
  const drawFrame = settings.drawFrame || function () {}
  const refreshGeometry = settings.refreshGeometry || function () {}
  const listenResize = settings.listenResize || function (refresh) {
    if (!browserWindow) return function () {}
    browserWindow.addEventListener('resize', refresh, { passive: true })
    return function () { browserWindow.removeEventListener('resize', refresh) }
  }
  const observeResize = settings.observeResize || function () { return function () {} }
  let destroyed = false
  let frameId = null
  let paused = false

  function refresh() {
    if (!destroyed) {
      refreshGeometry()
      scheduleFrame()
    }
  }

  function tick(now) {
    if (destroyed || paused) return
    frameId = null
    const keepDrawing = drawFrame(now)
    if (keepDrawing !== false) scheduleFrame()
  }

  function scheduleFrame() {
    if (destroyed || paused || frameId !== null) return
    frameId = requestFrame(tick)
  }

  const unlistenResize = listenResize(refresh)
  const disconnectObserver = observeResize(refresh)
  scheduleFrame()

  return {
    refresh,
    pause() {
      if (destroyed || paused) return
      paused = true
      if (frameId !== null && cancelFrame) cancelFrame(frameId)
      frameId = null
    },
    resume() {
      if (destroyed || !paused) return
      paused = false
      scheduleFrame()
    },
    destroy() {
      if (destroyed) return
      destroyed = true
      if (frameId !== null && cancelFrame) cancelFrame(frameId)
      if (typeof unlistenResize === 'function') unlistenResize()
      if (typeof disconnectObserver === 'function') disconnectObserver()
    }
  }
}

function pointAlongPolyline(points, progress) {
  const lengths = points.slice(1).map(function (point, index) {
    return segmentLength(points[index], point)
  })
  const total = lengths.reduce(function (sum, length) { return sum + length }, 0)
  let remaining = normalizeProgress(progress) * total

  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1]
    const end = points[index]
    const length = lengths[index - 1]
    if (remaining <= length || index === points.length - 1) {
      const ratio = length ? Math.max(0, Math.min(1, remaining / length)) : 0
      return {
        x: start.x + (end.x - start.x) * ratio,
        y: start.y + (end.y - start.y) * ratio
      }
    }
    remaining -= length
  }

  return points[points.length - 1]
}

function createCanvasLayer(parent, className) {
  const canvas = parent.ownerDocument.createElement('canvas')
  canvas.className = className
  canvas.setAttribute('aria-hidden', 'true')
  parent.appendChild(canvas)
  return canvas
}

function measureCanvas(canvas, windowObject) {
  const width = canvas.clientWidth
  const height = canvas.clientHeight
  const dpr = windowObject.devicePixelRatio || 1
  const canvasRect = canvas.getBoundingClientRect()
  const visualScale = width ? canvasRect.width / width : 1
  const ratio = Math.min(2, dpr * visualScale)

  return { width, height, canvasRect, ratio }
}

function sizeCanvas(canvas, context, windowObject) {
  const metrics = measureCanvas(canvas, windowObject)
  const width = metrics.width
  const height = metrics.height
  const ratio = metrics.ratio
  const pixelWidth = Math.max(1, Math.round(width * ratio))
  const pixelHeight = Math.max(1, Math.round(height * ratio))

  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth
    canvas.height = pixelHeight
  }
  context.setTransform(ratio, 0, 0, ratio, 0, 0)
  context.clearRect(0, 0, width, height)
  return metrics
}

function roundedRectPath(context, x, y, width, height, radius) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2))
  context.beginPath()
  context.moveTo(x + r, y)
  context.lineTo(x + width - r, y)
  context.arcTo(x + width, y, x + width, y + r, r)
  context.lineTo(x + width, y + height - r)
  context.arcTo(x + width, y + height, x + width - r, y + height, r)
  context.lineTo(x + r, y + height)
  context.arcTo(x, y + height, x, y + height - r, r)
  context.lineTo(x, y + r)
  context.arcTo(x, y, x + r, y, r)
  context.closePath()
}

function localFrame(element, canvas, canvasRect) {
  const elementRect = element.getBoundingClientRect()
  const scaleX = canvasRect.width ? canvas.clientWidth / canvasRect.width : 1
  const scaleY = canvasRect.height ? canvas.clientHeight / canvasRect.height : 1
  return {
    x: (elementRect.left - canvasRect.left) * scaleX,
    y: (elementRect.top - canvasRect.top) * scaleY,
    width: elementRect.width * scaleX,
    height: elementRect.height * scaleY
  }
}

function drawBoundary(context, canvas, element, now, options) {
  const canvasMetrics = sizeCanvas(canvas, context, options.windowObject)
  const frame = localFrame(element, canvas, canvasMetrics.canvasRect)
  const inset = 0.75
  const radius = options.radius

  context.save()
  roundedRectPath(
    context,
    frame.x + inset,
    frame.y + inset,
    Math.max(1, frame.width - inset * 2),
    Math.max(1, frame.height - inset * 2),
    radius
  )
  context.lineWidth = 0.58
  context.strokeStyle = 'rgba(71,222,255,.34)'
  context.shadowColor = 'rgba(76,218,255,.28)'
  context.shadowBlur = 2
  context.stroke()
  context.restore()

  if (options.reducedMotion || frame.width <= 0 || frame.height <= 0) return

  const progress = ((now + options.cycleOffset) % options.cycle) / options.cycle
  const samples = canvasMetrics.ratio < 1.5 ? options.scaledSamples : options.samples
  const tailRatio = options.tailRatio
  ;[0, 0.25, 0.5, 0.75].forEach(function (phase, beam) {
    let previous = null
    let previousPosition = null
    for (let sample = 0; sample <= samples; sample += 1) {
      const tailProgress = sample / samples
      const position = normalizeProgress(progress + phase - tailRatio + tailProgress * tailRatio)
      const localPoint = pointOnRoundedFrame(frame.width, frame.height, radius, position)
      const point = { x: frame.x + localPoint.x, y: frame.y + localPoint.y }
      if (previous && previousPosition !== null && Math.abs(position - previousPosition) < 0.08) {
        const strength = Math.pow(tailProgress, 1.72)
        const hue = (now / 52 + beam * 91 + tailProgress * 86 + options.cycleOffset / 34) % 360
        context.save()
        context.beginPath()
        context.moveTo(previous.x, previous.y)
        context.lineTo(point.x, point.y)
        context.lineCap = 'round'
        context.lineWidth = 0.24 + strength * 1.18
        context.strokeStyle = `hsla(${hue},100%,${63 + strength * 20}%,${0.035 + strength * 0.94})`
        context.shadowColor = `hsla(${hue},100%,72%,${0.16 + strength * 0.64})`
        context.shadowBlur = 1 + strength * 4
        context.stroke()
        context.restore()
      }
      previous = point
      previousPosition = position
    }
  })
}

function drawConnector(context, canvas, points, now, reducedMotion, windowObject) {
  const canvasMetrics = sizeCanvas(canvas, context, windowObject)
  if (!points || !points.length) return

  const first = points[0]
  const last = points[points.length - 1]
  const gradient = context.createLinearGradient(first.x, first.y, last.x, last.y)
  gradient.addColorStop(0, 'rgba(55,239,255,.42)')
  gradient.addColorStop(0.34, 'rgba(137,95,255,.42)')
  gradient.addColorStop(0.68, 'rgba(255,73,191,.44)')
  gradient.addColorStop(1, 'rgba(255,224,86,.58)')
  context.save()
  context.beginPath()
  context.moveTo(first.x, first.y)
  points.slice(1).forEach(function (point) { context.lineTo(point.x, point.y) })
  context.lineCap = 'round'
  context.lineJoin = 'round'
  context.lineWidth = 0.62
  context.strokeStyle = gradient
  context.shadowColor = 'rgba(70,229,255,.45)'
  context.shadowBlur = 2.3
  context.stroke()
  context.restore()

  ;[first, last].forEach(function (point, index) {
    const hue = (now / 40 + index * 155) % 360
    context.save()
    context.beginPath()
    context.arc(point.x, point.y, index ? 1.55 : 1.8, 0, Math.PI * 2)
    context.fillStyle = `hsla(${hue},100%,82%,.98)`
    context.shadowColor = `hsla(${hue},100%,72%,.95)`
    context.shadowBlur = 7
    context.fill()
    context.restore()
  })

  if (reducedMotion) return
  const cycle = 9000
  const tailRatio = 0.46
  const samples = canvasMetrics.ratio < 1.5 ? 48 : 180
  ;[0, 0.5].forEach(function (phase, beam) {
    const progress = ((now % cycle) / cycle + phase) % 1
    let previous = null
    let previousPosition = null
    for (let sample = 0; sample <= samples; sample += 1) {
      const tailProgress = sample / samples
      const position = normalizeProgress(progress - tailRatio + tailProgress * tailRatio)
      const point = pointAlongPolyline(points, position)
      if (previous && previousPosition !== null && Math.abs(position - previousPosition) < 0.08) {
        const strength = Math.pow(tailProgress, 1.7)
        const hue = (now / 44 + beam * 170 + tailProgress * 135) % 360
        context.save()
        context.beginPath()
        context.moveTo(previous.x, previous.y)
        context.lineTo(point.x, point.y)
        context.lineCap = 'round'
        context.lineWidth = 0.25 + strength * 1.08
        context.strokeStyle = `hsla(${hue},100%,${65 + strength * 17}%,${0.03 + strength * 0.9})`
        context.shadowColor = `hsla(${hue},100%,74%,${0.14 + strength * 0.58})`
        context.shadowBlur = 1 + strength * 3.4
        context.stroke()
        context.restore()
      }
      previous = point
      previousPosition = position
    }
  })
}

function createDownloadCenterFlowRenderer(options) {
  const settings = options || {}
  const matrix = settings.matrix
  const windowObject = settings.windowObject || (typeof window === 'undefined' ? null : window)
  const canvases = []
  const noopRenderer = { refresh() {}, destroy() {} }

  if (!matrix || !matrix.ownerDocument || !windowObject) return noopRenderer

  function addCanvas(className, parent) {
    const canvas = createCanvasLayer(parent || matrix, className)
    canvases.push(canvas)
    return canvas
  }

  function removeCanvases() {
    canvases.forEach(function (canvas) {
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas)
    })
  }

  let consoleCanvas
  let stageCanvas
  let connectorCanvas
  let consoleContext
  let stageContext
  let connectorContext
  try {
    consoleCanvas = addCanvas('flow-boundary-canvas flow-boundary-console')
    stageCanvas = addCanvas('flow-boundary-canvas flow-boundary-stage', settings.experienceStage)
    connectorCanvas = addCanvas('subordinate-link-canvas')
    consoleContext = consoleCanvas.getContext('2d')
    stageContext = stageCanvas.getContext('2d')
    connectorContext = connectorCanvas.getContext('2d')
  } catch (error) {
    removeCanvases()
    return noopRenderer
  }

  if (!consoleContext || !stageContext || !connectorContext) {
    removeCanvases()
    return noopRenderer
  }
  let connectorPoints = null
  let renderRatio = 2
  let lastDrawTime = null
  let destroyed = false

  function refreshGeometry() {
    renderRatio = measureCanvas(consoleCanvas, windowObject).ratio
    const source = settings.getActiveSource()
    if (!source) {
      connectorPoints = null
      connectorCanvas.style && (connectorCanvas.style.display = 'none')
      return
    }
    if (connectorCanvas.style && connectorCanvas.style.removeProperty) {
      connectorCanvas.style.removeProperty('display')
    }
    const canvasRect = connectorCanvas.getBoundingClientRect()
    const sourceRect = source.getBoundingClientRect()
    const panelRect = settings.channelPanel.getBoundingClientRect()
    const headingRect = settings.channelHeading.getBoundingClientRect()
    const scaleX = canvasRect.width ? connectorCanvas.clientWidth / canvasRect.width : 1
    const scaleY = canvasRect.height ? connectorCanvas.clientHeight / canvasRect.height : 1
    connectorPoints = buildConnectorPoints(
      {
        right: (sourceRect.right - canvasRect.left) * scaleX,
        top: (sourceRect.top - canvasRect.top) * scaleY,
        height: sourceRect.height * scaleY
      },
      {
        left: (panelRect.left - canvasRect.left) * scaleX,
        top: (headingRect.top - canvasRect.top) * scaleY,
        height: headingRect.height * scaleY
      },
      { left: 0, top: 0 }
    )
  }

  refreshGeometry()
  const requestFrame = windowObject.requestAnimationFrame
    ? windowObject.requestAnimationFrame.bind(windowObject)
    : function (callback) {
        return windowObject.setTimeout(function () { callback(Date.now()) }, 16)
      }
  const cancelFrame = windowObject.cancelAnimationFrame
    ? windowObject.cancelAnimationFrame.bind(windowObject)
    : windowObject.clearTimeout.bind(windowObject)
  const renderer = createFlowRenderer({
    requestFrame,
    cancelFrame,
    listenResize(refresh) {
      windowObject.addEventListener('resize', refresh, { passive: true })
      return function () { windowObject.removeEventListener('resize', refresh) }
    },
    observeResize(refresh) {
      if (!windowObject.ResizeObserver) return function () {}
      const observer = new windowObject.ResizeObserver(refresh)
      observer.observe(matrix)
      return function () { observer.disconnect() }
    },
    refreshGeometry,
    drawFrame(now) {
      if (renderRatio < 1.5 && lastDrawTime !== null && now - lastDrawTime < 1000 / 30) return
      lastDrawTime = now
      const reducedMotion = windowObject.matchMedia
        ? windowObject.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false
      drawBoundary(consoleContext, consoleCanvas, settings.channelPanel, now, {
        cycle: 9800,
        cycleOffset: 0,
        radius: 12,
        reducedMotion,
        samples: 125,
        scaledSamples: 32,
        tailRatio: 0.15,
        windowObject
      })
      drawBoundary(stageContext, stageCanvas, settings.experienceStage, now, {
        cycle: 12600,
        cycleOffset: 3100,
        radius: 14,
        reducedMotion,
        samples: 105,
        scaledSamples: 28,
        tailRatio: 0.12,
        windowObject
      })
      drawConnector(connectorContext, connectorCanvas, connectorPoints, now, reducedMotion, windowObject)
      return !reducedMotion
    }
  })
  const ownerDocument = matrix.ownerDocument
  const handleVisibilityChange = function () {
    if (ownerDocument.hidden) {
      renderer.pause()
      return
    }
    lastDrawTime = null
    renderer.resume()
  }
  if (ownerDocument.addEventListener) {
    ownerDocument.addEventListener('visibilitychange', handleVisibilityChange)
    if (ownerDocument.hidden) renderer.pause()
  }

  return {
    refresh: renderer.refresh,
    destroy() {
      if (destroyed) return
      destroyed = true
      if (ownerDocument.removeEventListener) {
        ownerDocument.removeEventListener('visibilitychange', handleVisibilityChange)
      }
      renderer.destroy()
      removeCanvases()
    }
  }
}

export default {
  buildConnectorPoints,
  createDownloadCenterFlowRenderer,
  createFlowRenderer,
  pointOnRoundedFrame,
  pointAlongPolyline
}



// WEBPACK FOOTER //
// ./src/download/download-center-flow.js